import {
  CheckCircle2,
  Clipboard,
  Download,
  ExternalLink,
  KeyRound,
  LoaderCircle,
  ShieldCheck,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

import { useSession } from "../context/SessionContext";
import { formatTime } from "../lib/format";

export function MfaEnrollment({ onComplete }: { onComplete: () => void }) {
  const {
    user,
    beginStaffMfaSetup,
    confirmStaffMfaSetup,
    refresh,
  } = useSession();
  const requiresSetup = user?.kind === "staff" && user.mfaSetupRequired;
  const [setup, setSetup] = useState<{
    secret: string;
    otpauthUri: string;
    expiresAt: string;
  } | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(requiresSetup);
  const [copied, setCopied] = useState<"secret" | "recovery" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!requiresSetup) {
      setBusy(false);
      return;
    }
    let active = true;
    setBusy(true);
    setError(null);
    void beginStaffMfaSetup()
      .then((result) => {
        if (active) setSetup(result);
      })
      .catch(async (requestError) => {
        if (!active) return;
        setError(requestError instanceof Error ? requestError.message : "MFA setup could not be started.");
        await refresh({ silentSso: false });
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [beginStaffMfaSetup, refresh, requiresSetup]);

  async function copy(value: string, type: "secret" | "recovery") {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(type);
      window.setTimeout(() => setCopied((current) => current === type ? null : current), 1800);
    } catch {
      setError("Copy was blocked by this browser. Select the text and copy it manually.");
    }
  }

  async function confirm(event: FormEvent) {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await confirmStaffMfaSetup(code);
      setRecoveryCodes(result.recoveryCodes);
      setCode("");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The authenticator code could not be verified.");
    } finally {
      setBusy(false);
    }
  }

  function downloadRecoveryCodes() {
    const content = [
      "Next-Cart Support recovery codes",
      "Each code can be used once. Store these codes somewhere private.",
      "",
      ...recoveryCodes,
      "",
    ].join("\n");
    const url = URL.createObjectURL(new Blob([content], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "nextcart-support-recovery-codes.txt";
    link.click();
    URL.revokeObjectURL(url);
  }

  if (recoveryCodes.length) {
    return (
      <section className="settings-section card" aria-labelledby="mfa-recovery-title">
        <header>
          <div>
            <p className="eyebrow">Final step</p>
            <h2 id="mfa-recovery-title">Save your recovery codes</h2>
            <p>These codes are shown once. Each code replaces your authenticator for one sign-in.</p>
          </div>
          <span className="status-text status-text--active"><i />MFA enabled</span>
        </header>
        <div className="preference-list">
          <article>
            <span><CheckCircle2 aria-hidden="true" /></span>
            <div>
              <strong>Recovery codes</strong>
              <p>Keep them in a password manager or another secure offline location.</p>
              <div role="list" aria-label="One-time recovery codes">
                {recoveryCodes.map((recoveryCode) => (
                  <code className="tag" role="listitem" key={recoveryCode}>{recoveryCode}</code>
                ))}
              </div>
            </div>
            <div>
              <button className="button button--secondary button--small" type="button" onClick={() => void copy(recoveryCodes.join("\n"), "recovery")}>
                <Clipboard aria-hidden="true" size={15} />{copied === "recovery" ? "Copied" : "Copy all"}
              </button>
              <button className="button button--quiet button--small" type="button" onClick={downloadRecoveryCodes}>
                <Download aria-hidden="true" size={15} />Download
              </button>
            </div>
          </article>
          <article>
            <span><ShieldCheck aria-hidden="true" /></span>
            <div>
              <label className="check-field">
                <input type="checkbox" checked={saved} onChange={(event) => setSaved(event.target.checked)} />
                <span>I saved these recovery codes in a secure place.</span>
              </label>
            </div>
            <button className="button button--primary" type="button" disabled={!saved} onClick={onComplete}>
              Continue to workspace
            </button>
          </article>
        </div>
      </section>
    );
  }

  if (!requiresSetup) {
    return (
      <section className="settings-section card">
        <header><div><h2>Authenticator app enabled</h2><p>This staff account is protected by multi-factor authentication.</p></div></header>
        <div className="security-summary">
          <span><ShieldCheck aria-hidden="true" /></span>
          <div><strong>Setup complete</strong><p>Recovery codes cannot be displayed again after enrollment.</p></div>
          <span className="status-text status-text--active"><i />Protected</span>
          <button className="button button--primary button--small" type="button" onClick={onComplete}>Continue</button>
        </div>
      </section>
    );
  }

  return (
    <section className="settings-section card" aria-labelledby="mfa-setup-title" aria-busy={busy}>
      <header>
        <div>
          <p className="eyebrow">Required security setup</p>
          <h2 id="mfa-setup-title">Protect your staff account</h2>
          <p>Complete this setup before opening tickets, customer records, or admin tools.</p>
        </div>
        <span className="status-text"><i />Restricted session</span>
      </header>
      {error && <div className="form-alert" role="alert">{error}</div>}
      {busy && !setup ? (
        <div className="security-summary" role="status">
          <span><LoaderCircle aria-hidden="true" /></span>
          <div><strong>Preparing a secure secret</strong><p>This should only take a moment.</p></div>
        </div>
      ) : setup ? (
        <div className="preference-list">
          <article className="mfa-setup__enrollment">
            <span><KeyRound aria-hidden="true" /></span>
            <div className="mfa-setup__content">
              <strong>1. Add Next-Cart Support to your authenticator</strong>
              <p>Scan the QR code, open the link on this device, or enter the secret in any TOTP-compatible authenticator app.</p>
              <div className="mfa-setup__credentials">
                <div className="mfa-setup__qr">
                  <QRCodeSVG
                    value={setup.otpauthUri}
                    size={176}
                    level="M"
                    marginSize={2}
                    title="Scan to add Next-Cart Support to your authenticator app"
                  />
                </div>
                <label className="field mfa-setup__secret">
                  <span>Manual setup secret</span>
                  <input value={setup.secret} readOnly spellCheck={false} onFocus={(event) => event.currentTarget.select()} />
                  <small>Expires {formatTime(setup.expiresAt)}</small>
                </label>
              </div>
            </div>
            <div className="mfa-setup__actions">
              <a className="button button--primary button--small" href={setup.otpauthUri}>
                <ExternalLink aria-hidden="true" size={15} />Open app
              </a>
              <button className="button button--secondary button--small" type="button" onClick={() => void copy(setup.secret, "secret")}>
                <Clipboard aria-hidden="true" size={15} />{copied === "secret" ? "Copied" : "Copy secret"}
              </button>
            </div>
          </article>
          <article>
            <span><ShieldCheck aria-hidden="true" /></span>
            <div>
              <strong>2. Verify the first code</strong>
              <p>Enter the current 6-digit code. The clock on your device must be accurate.</p>
              <form onSubmit={confirm}>
                <label className="field mfa-field">
                  <span>Authentication code</span>
                  <div>
                    <KeyRound aria-hidden="true" size={18} />
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      aria-describedby="mfa-code-hint"
                      placeholder="000000"
                    />
                  </div>
                  <small id="mfa-code-hint">Codes change every 30 seconds.</small>
                </label>
                <button className="button button--primary" type="submit" disabled={busy || code.length !== 6}>
                  {busy ? "Verifying…" : "Enable MFA"}
                </button>
              </form>
            </div>
          </article>
        </div>
      ) : (
        <div className="security-summary">
          <span><KeyRound aria-hidden="true" /></span>
          <div><strong>Setup is unavailable</strong><p>Retry the request or sign out and sign in again.</p></div>
          <button className="button button--secondary" type="button" onClick={() => void refresh({ silentSso: false })}>Retry</button>
        </div>
      )}
    </section>
  );
}
